import CommonCss from "nlib/common/common.module.scss";

import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Button } from "nlib/ui";
import { NavLink, Redirect } from "react-router-dom";
import { checkIsBusinessUser } from "selectors/user";
import {
  checkSelectedBusinessHasBusinessUsers,
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getCurrentXeroOrganizationId,
  getGlobalStats,
  getSelectedBusinessData
} from "selectors/businesses";
import { getActiveOrganization } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import Banner from "./lib/Banner";
import Countries from "const/Countries";
import DocumentsAwaitingReviewCounter from "./lib/DocumentsAwaitingReviewCounter";
import DocumentsUnpaidCounter from "./lib/DocumentsUnpaidCounter";
import LastActivity from "./lib/LastActivity";
import MobileHeader from "mlib/common/MobileHeader";
import MonthEndCloseCounter from "./lib/MonthEndCloseCounter";
import Page from "nlib/common/Page";
import PageContent from "nlib/common/PageContent";
import PageHeader from "nlib/common/PageHeader";
import Platforms from "utils/Platforms";
import React from "react";
import TasksCounter from "./lib/TasksCounter";
import TransactionsAwaitingClassificationCounter from "./lib/TransactionsAwaitingClassificationCounter";
import TransactionsAwaitingClientResponseCounter from "./lib/TransactionsAwaitingClientResponseCounter";
import TransactionsAwaitingReviewCounter from "./lib/TransactionsAwaitingReviewCounter";
import TransactionsNeedDocumentCounter from "./lib/TransactionsNeedDocumentCounter";
import UiRoutes from "const/UiRoutes";
import classNames from "classnames";

/* eslint-disable no-magic-numbers */
const COUNTERS_COLUMNS = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5
};
/* eslint-enable no-magic-numbers */

const getCountersColumns = (countersCount) => {
  if (countersCount <= COUNTERS_COLUMNS.THREE) return Math.max(countersCount, COUNTERS_COLUMNS.ONE);
  if (countersCount % COUNTERS_COLUMNS.FOUR !== COUNTERS_COLUMNS.ONE) return COUNTERS_COLUMNS.FOUR;
  if (
    countersCount >= COUNTERS_COLUMNS.FIVE
    && countersCount % COUNTERS_COLUMNS.FIVE !== COUNTERS_COLUMNS.ONE
  ) return COUNTERS_COLUMNS.FIVE;
  if (countersCount % COUNTERS_COLUMNS.THREE !== COUNTERS_COLUMNS.ONE) return COUNTERS_COLUMNS.THREE;

  return COUNTERS_COLUMNS.TWO;
};
/* eslint-enable no-magic-numbers */

const DashboardPage = ({ className }) => {
  const { uiTexts } = useSelector(getTextsData);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const { countryCode } = useSelector(getActiveOrganization);

  const businessUser = useSelector(checkIsBusinessUser);

  const {
    transactions: {
      needReaction: { count: needReactionCount } = {}
    } = {}
  } = useSelector(getGlobalStats);

  const czCountry = countryCode === Countries.CZ;

  const {
    id: businessId,
    settings: { advancedDocumentsWorkflow } = {}
  } = useSelector(getSelectedBusinessData);

  const selectedBusinessHasBusinessUsers = useSelector(checkSelectedBusinessHasBusinessUsers);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const touchDevice = Platforms.checkIsTouchDevice();

  const counters = [
    ...(czCountry
      ? []
      : [
        <TasksCounter key="tasks" />,
        <TransactionsAwaitingClassificationCounter key="toReconcile" />,
        ...(!touchDevice && rpaMode ? [<TransactionsNeedDocumentCounter key="needDocument" />] : []),
        <TransactionsAwaitingReviewCounter key="toReport" />,
        ...(selectedBusinessHasBusinessUsers || !!needReactionCount || rpaMode
          ? [<TransactionsAwaitingClientResponseCounter key="needReaction" />]
          : []),
        ...(!touchDevice && (quickBooksBusiness || xeroBusiness) ? [<MonthEndCloseCounter key="monthEndClose" />] : [])
      ]),
    ...(advancedDocumentsWorkflow ? [
      <DocumentsAwaitingReviewCounter key="documentsAwaitingReview" />,
      ...(rpaMode ? [<DocumentsUnpaidCounter key="documentsUnpaid" />] : [])
    ] : [])
  ];

  const countersColumns = getCountersColumns(counters.length);

  if (businessUser) return <Redirect to={`/${businessId}${czCountry ? UiRoutes.DOCUMENTS : UiRoutes.TRANSACTIONS}`} />;

  return (
    <>
      {touchDevice && <MobileHeader />}
      <Page className={classNames(Css.dashboardPage, className)}>
        <PageContent>
          {!touchDevice && (
            <>
              <PageHeader />
              <Banner />
            </>
          )}
          <div
            className={Css.counters}
            style={{ gridTemplateColumns: `repeat(${countersColumns}, minmax(0, 1fr))` }}>
            {counters}
          </div>
          <div className={Css.cards}>
            <div className={classNames(CommonCss.card, Css.card)}>
              <div className={Css.header}>
                <div className={Css.title}>
                  <Icons.Lightning />
                  <span>{uiTexts.recentActivity}</span>
                </div>
                <NavLink to={`/${businessId}${UiRoutes.ACTIVITY}`}>
                  <Button outline>{uiTexts.showAll}</Button>
                </NavLink>
              </div>
              <LastActivity />
            </div>
          </div>
        </PageContent>
      </Page>
    </>
  );
};

export default React.memo(DashboardPage);
